import { FeedbackSubmission, FeedbackRating, FeedbackCategory } from '@/types/feedback';
import { notifyError } from '@/services/error';

/**
 * Collect diagnostic information about the client environment
 */
function collectDiagnosticInfo(): Record<string, unknown> {
  const info: Record<string, unknown> = {};

  // Browser and Window Information
  if (typeof window !== 'undefined') {
    const { userAgent, language, platform } = window.navigator;
    info.userAgent = userAgent;
    info.language = language;
    info.platform = platform;
    info.viewportWidth = window.innerWidth;
    info.viewportHeight = window.innerHeight;
    info.devicePixelRatio = window.devicePixelRatio;
    info.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Screen Information
    if (window.screen) {
      info.screenWidth = window.screen.width;
      info.screenHeight = window.screen.height;
      info.screenColorDepth = window.screen.colorDepth;
    }

    // Performance Information
    if (window.performance) {
      const navigation = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        info.pageLoadTime = navigation.loadEventEnd - navigation.startTime;
        info.domReadyTime = navigation.domContentLoadedEventEnd - navigation.startTime;
      }
    }

    // Network Information
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        info.networkType = connection.effectiveType;
        info.networkDownlink = connection.downlink;
        info.networkRtt = connection.rtt;
      }
    }

    // Memory Information
    if ('memory' in window.performance) {
      const memory = (window.performance as any).memory;
      if (memory) {
        info.totalJSHeapSize = memory.totalJSHeapSize;
        info.usedJSHeapSize = memory.usedJSHeapSize;
      }
    }
  }

  return info;
}

/**
 * Convert legacy rating strings to new enum values
 */
function convertLegacyRating(rating: string): FeedbackRating {
  switch (rating.toLowerCase()) {
    case 'positive':
      return FeedbackRating.Positive;
    case 'negative':
      return FeedbackRating.Negative;
    case 'neutral':
    default:
      return FeedbackRating.Neutral;
  }
}

/**
 * Submit feedback using the unified REST API
 */
export async function submitFeedback(data: FeedbackSubmission): Promise<Response> {
  try {
    // Add diagnostic information and timestamp
    const submission: FeedbackSubmission = {
      ...data,
      diagnostic: {
        ...collectDiagnosticInfo(),
        timestamp: new Date().toISOString(),
        ...data.diagnostic
      },
      metadata: {
        timestamp: new Date().toISOString(),
        ...data.metadata
      }
    };

    console.log('Submitting feedback:', submission);

    const response = await fetch('/api/feedback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submission),
    });

    if (!response.ok) {
      let errorDetails;
      try {
        const errorJson = await response.json();
        errorDetails = errorJson.error || JSON.stringify(errorJson);
      } catch {
        errorDetails = await response.text().catch(() => 'Could not read response');
      }
      
      console.error('Feedback submission failed:', {
        status: response.status,
        statusText: response.statusText,
        details: errorDetails
      });
      
      throw new Error(`Failed to submit feedback: ${response.statusText} (${errorDetails})`);
    }

    return response;
  } catch (_error) {
    console.error('Error in submitFeedback:', _error);
    notifyError(
      _error instanceof Error ? _error : new Error('Failed to submit feedback'),
      {
        context: 'feedbackService.submitFeedback',
        originalError: _error,
        feedbackData: data,
      }
    );
    throw _error;
  }
}

/**
 * Submit feedback via SignalR (for copilot integration)
 */
export async function submitFeedbackViaSignalR(
  send: (method: string, ...args: any[]) => Promise<void>,
  data: FeedbackSubmission
): Promise<void> {
  try {
    // Add diagnostic information and timestamp
    const submission: FeedbackSubmission = {
      ...data,
      diagnostic: {
        ...collectDiagnosticInfo(),
        timestamp: new Date().toISOString(),
        ...data.diagnostic
      },
      metadata: {
        timestamp: new Date().toISOString(),
        ...data.metadata
      }
    };

    console.log('Submitting feedback via SignalR:', submission);

    await send('SubmitFeedback', submission);
  } catch (_error) {
    console.error('Error in submitFeedbackViaSignalR:', _error);
    notifyError(
      _error instanceof Error ? _error : new Error('Failed to submit feedback via SignalR'),
      {
        context: 'feedbackService.submitFeedbackViaSignalR',
        originalError: _error,
        feedbackData: data,
      }
    );
    throw _error;
  }
}

/**
 * Helper function to create a FeedbackSubmission from legacy data
 */
export function createFeedbackSubmission(params: {
  rating: 'positive' | 'negative' | 'neutral';
  comments: string;
  itemName: string;
  category?: FeedbackCategory;
  pageUrl?: string;
  interactionId?: string;
  questionId?: string;
  transcriptId?: string;
  evaluationId?: string;
  serverVersion?: string;
  serverBuildTime?: string;
}): FeedbackSubmission {
  return {
    category: params.category || FeedbackCategory.General,
    rating: convertLegacyRating(params.rating),
    itemName: params.itemName,
    comments: params.comments,
    pageUrl: params.pageUrl || (typeof window !== 'undefined' ? window.location.href : 'unknown'),
    interactionId: params.interactionId,
    questionId: params.questionId,
    transcriptId: params.transcriptId,
    evaluationId: params.evaluationId,
    serverVersion: params.serverVersion,
    serverBuildTime: params.serverBuildTime
  };
}